import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Mail, ArrowRight, ArrowLeft, MailCheck } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SEO } from "@/components/shared/SEO";
import { AuthBrandPanel } from "@/components/auth/AuthBrandPanel";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5 bg-[color:var(--sg-bg)] overflow-x-hidden">
      <SEO title="Forgot password" path="/forgot-password" />
      {/* Left brand pane — memorable SociBaba touch. */}
      <div className="lg:col-span-2">
        <AuthBrandPanel variant="forgot" />
      </div>
      {/* Right form pane */}
      <div className="lg:col-span-3 flex flex-col justify-center p-6 lg:p-14 relative">
        <div className="w-full max-w-md mx-auto">
          <Link
            to="/login"
            data-testid="forgot-back-login"
            className="inline-flex items-center gap-1.5 text-sm text-[color:var(--sg-muted)] hover:text-[color:var(--sg-fg)] font-medium mb-6"
          >
            <ArrowLeft className="h-4 w-4" /> Back to login
          </Link>
          <div className="sg-label mb-3">Password reset</div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight mb-2">
            Reset your password
          </h1>
          <p className="text-sm text-[color:var(--sg-muted)] mb-8 leading-relaxed">
            Enter the email you used to sign up and we&apos;ll send a secure reset link. It expires in 30 minutes so no bad actor can sit on it.
          </p>

          {sent ? (
            <div
              data-testid="forgot-sent"
              className="rounded-2xl bg-gradient-to-br from-emerald-50 to-cyan-50 border border-emerald-200 p-6"
            >
              <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center shadow-lg mb-4">
                <MailCheck className="h-6 w-6 text-white" />
              </div>
              <div className="font-display text-lg font-bold text-emerald-900 mb-1">Check your inbox</div>
              <p className="text-sm text-emerald-900/80 leading-relaxed">
                If <span className="font-semibold text-emerald-900">{email}</span> is a SociBaba™ account, a reset link is on its way.
                Didn&apos;t get it in a couple of minutes? Check spam, or ping{" "}
                <a href="mailto:support@socibaba.com" className="font-semibold underline">support@socibaba.com</a>.
              </p>
              <button
                onClick={() => { setSent(false); setEmail(""); }}
                className="mt-4 text-xs font-semibold text-emerald-800 hover:text-emerald-950 underline"
              >
                Try a different email
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
              <div className="space-y-2">
                <Label htmlFor="forgot-email" className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">
                  Account email
                </Label>
                <div className="relative group">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[color:var(--sg-muted)] group-focus-within:text-[color:var(--sg-primary)] transition-colors" />
                  <Input
                    id="forgot-email"
                    data-testid="forgot-email"
                    type="email"
                    placeholder="you@brand.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="pl-9 h-11 bg-white"
                    autoComplete="email"
                    autoFocus
                  />
                </div>
              </div>

              <Button
                type="submit"
                data-testid="forgot-submit"
                disabled={busy}
                className="w-full h-11 group relative overflow-hidden"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-orange-500 via-fuchsia-500 to-cyan-500 bg-[length:200%_100%] group-hover:brightness-110 animate-[gradient_6s_ease_infinite]" />
                <span className="relative flex items-center justify-center gap-1.5 text-white font-semibold">
                  {busy ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>Send reset link <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" /></>
                  )}
                </span>
              </Button>

              <div className="rounded-xl bg-slate-50 dark:bg-slate-800/50 p-3 text-xs text-[color:var(--sg-muted)] leading-relaxed">
                <span className="font-semibold text-[color:var(--sg-fg)]">Privacy note:</span>{" "}
                We always respond &quot;email sent&quot; even if the address isn&apos;t on file — that&apos;s how we stop attackers from figuring out which emails have SociBaba accounts.
              </div>
            </form>
          )}

          <p className="text-sm text-[color:var(--sg-muted)] mt-8 text-center">
            Remember it after all?{" "}
            <Link to="/login" className="text-[color:var(--sg-primary)] font-semibold hover:underline">
              Log in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
